import { Link } from "react-router-dom";
import { timeAgo } from "@/lib/api";
import { PolicyHead, SubthemeChip } from "./PolicyIntelligence";
import { sectorTrack } from "./SectorHero";

const WatchRow = ({ it, testid }: { it: any; testid: string }) => (
  <Link to={`/article/${it.slug}`} data-testid={testid}
    onClick={() => sectorTrack("enforcement_click", it.slug, "policy")}
    className="group block py-3 first:pt-1 last:pb-1">
    <div className="flex items-center gap-2 flex-wrap">
      {it.regulator && (
        <span className="rounded border border-[#F87171]/35 bg-[#F87171]/[0.08] px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-[0.14em] text-[#F87171]">
          {it.regulator}
        </span>
      )}
      {it.jurisdiction && (
        <span className="rounded border border-white/[0.16] bg-black/40 px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-[0.14em] text-gray-300">
          {it.jurisdiction}
        </span>
      )}
      <span className="font-mono text-[8.5px] uppercase tracking-wide text-gray-600 ml-auto">{timeAgo(it.published_at)}</span>
    </div>
    <p className="font-news font-bold text-white text-[14px] leading-snug mt-1.5 group-hover:text-[#F87171] transition-colors">
      {it.title}
    </p>
  </Link>
);

/* ENFORCEMENT WATCH (§22-25): article-driven and labeled as such — validated
   Policy stories whose dominant subtheme is Enforcement. Never claims structured
   completeness (no "all actions"/"total fines"). Courts stay a distinct rail and
   are never merged into Enforcement to create volume. */
export const EnforcementWatch = ({ items, courts }: { items: any[]; courts: any[] }) => {
  if (!items?.length && !courts?.length) return null;
  const hasCourts = (courts || []).length > 0;
  return (
    <section data-testid="policy-enforcement" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12">
      <PolicyHead title="Enforcement Watch" chip="Article-driven" testid="policy-enforcement-head"
        desc="Latest material digital-asset enforcement developments — validated coverage, not a structured enforcement-actions database." />
      <div className={`grid gap-4 items-start ${hasCourts ? "lg:grid-cols-[minmax(0,1fr)_360px]" : ""}`}>
        {items.length > 0 && (
          <div className="rounded-xl border border-white/[0.08] bg-[#0a0b10] px-4 sm:px-5 py-2 divide-y divide-white/[0.06]">
            {items.map((it, i) => <WatchRow key={it.slug} it={it} testid={`enforcement-item-${i}`} />)}
          </div>
        )}
        {hasCourts && (
          <div data-testid="policy-courts"
            className="rounded-xl border border-white/[0.08] bg-[#0a0b10] px-4 py-2">
            <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.26em] text-gray-300 pt-2">Courts &amp; Legal</p>
            <div className="divide-y divide-white/[0.06]">
              {courts.map((it, i) => <WatchRow key={it.slug} it={it} testid={`courts-item-${i}`} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

/* LEGISLATION & RULEMAKING (§26): significant validated reporting; confirmed
   Regulation Tracker relationships appear as clearly marked TRACKED chips —
   articles are never presented as structured legislation records. */
export const LegislationRulemaking = ({ data }: { data: any }) => {
  const items = data?.developments || [];
  if (!items.length) return null;
  return (
    <section data-testid="policy-legislation" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12">
      <PolicyHead title="Legislation & Rulemaking" testid="policy-legislation-head"
        desc="Significant validated legislative and rulemaking developments. TRACKED chips mark confirmed relationships to structured Regulation Tracker records." />
      <div className="rounded-xl border border-white/[0.08] bg-[#0a0b10] px-4 sm:px-5 py-2 divide-y divide-white/[0.06]">
        {items.map((it: any, i: number) => (
          <div key={it.slug} className="py-3 first:pt-2 last:pb-2" data-testid={`legislation-item-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <SubthemeChip s={it.subtheme} />
              <span className="font-mono text-[8.5px] uppercase tracking-wide text-gray-600 ml-auto">{timeAgo(it.published_at)}</span>
            </div>
            <Link to={`/article/${it.slug}`}
              onClick={() => sectorTrack("legislation_click", it.slug, "policy")}
              className="group block font-news font-bold text-white text-[14px] leading-snug mt-1.5 hover:text-[#A78BFA] transition-colors">
              {it.title}
            </Link>
            {(it.linked_regulations || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {it.linked_regulations.map((r: any) => (
                  <Link key={r.slug} to={`/regulation/${r.slug}`}
                    onClick={() => sectorTrack("regulation_click", r.slug, "policy")}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-[#A78BFA]/25 bg-[#A78BFA]/[0.05] px-2.5 py-1 hover:border-[#A78BFA]/55 transition-colors">
                    <span className="font-mono text-[7.5px] font-bold tracking-[0.16em] text-[#A78BFA]">TRACKED</span>
                    <span className="font-mono text-[9.5px] text-gray-300 max-w-[220px] truncate group-hover:text-white transition-colors">{r.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
